/**
 * Debug utilities for responsive issues
 * Usage: Open browser console and type enableDebugMode()
 */

declare global {
  interface Window {
    enableDebugMode: () => void;
    disableDebugMode: () => void;
    showBreakpoint: () => void;
    hideBreakpoint: () => void;
    showGrid: () => void;
    hideGrid: () => void;
    findOverflowElements: () => void;
  }
}

// Enable all debug features
export function enableDebugMode() {
  document.body.classList.add('debug-overflow');
  document.body.classList.add('show-breakpoint');
  document.body.classList.remove('overflow-fixed'); // Allow overflow to be visible
  console.log('🐛 Debug mode enabled');
  console.log('📱 Breakpoint indicator: bottom-right corner');
  console.log('🔴 Overflow elements: highlighted in red');
  console.log('✅ Horizontal scroll enabled to see overflow');
  console.log('Commands:');
  console.log('  - disableDebugMode(): Disable all debug features');
  console.log('  - showGrid(): Show grid overlay');
  console.log('  - findOverflowElements(): Find elements causing overflow');
  
  // Trigger a check immediately
  setTimeout(() => findOverflowElements(), 100);
}

// Disable all debug features
export function disableDebugMode() {
  document.body.classList.remove('debug-overflow');
  document.body.classList.remove('show-breakpoint');
  document.body.classList.remove('show-grid');
  document.body.classList.add('overflow-fixed'); // Hide overflow again
  console.log('✅ Debug mode disabled');
  console.log('🔒 Horizontal overflow hidden again');
}

// Show breakpoint indicator
export function showBreakpoint() {
  document.body.classList.add('show-breakpoint');
  console.log('📱 Breakpoint indicator shown');
}

// Hide breakpoint indicator
export function hideBreakpoint() {
  document.body.classList.remove('show-breakpoint');
  console.log('📱 Breakpoint indicator hidden');
}

// Show grid overlay (helpful for alignment debugging)
export function showGrid() {
  document.body.classList.add('show-grid');
  console.log('📐 Grid overlay shown (press Ctrl+Shift+G or call hideGrid())');
}

// Hide grid overlay
export function hideGrid() {
  document.body.classList.remove('show-grid');
  console.log('📐 Grid overlay hidden');
}

// Find all elements causing horizontal overflow
export function findOverflowElements() {
  const windowWidth = window.innerWidth;
  const allElements = document.querySelectorAll('*');
  const overflowElements: Array<{ element: HTMLElement; selector: string; width: number; overflow: number; rightEdge: number }> = [];

  allElements.forEach((el) => {
    const rect = el.getBoundingClientRect();
    const rightEdge = rect.right;
    
    // Check if element extends beyond viewport
    if (rect.width > windowWidth || rightEdge > windowWidth) {
      const selector = `${el.tagName}${el.id ? `#${el.id}` : ''}${el.className ? `.${Array.from(el.classList).slice(0, 2).join('.')}` : ''}`;
      overflowElements.push({
        element: el as HTMLElement,
        selector,
        width: Math.round(rect.width),
        overflow: Math.max(
          Math.round(rect.width - windowWidth),
          Math.round(rightEdge - windowWidth)
        ),
        rightEdge: Math.round(rightEdge)
      });
    }
  });

  if (overflowElements.length === 0) {
    console.log('✅ No overflow elements found!');
    return;
  }

  console.group(`🔴 Found ${overflowElements.length} elements causing horizontal overflow`);
  console.log('Window width:', windowWidth);
  console.log('Viewport right edge:', windowWidth);
  
  // Sort by overflow amount descending
  overflowElements
    .sort((a, b) => b.overflow - a.overflow)
    .slice(0, 20) // Show top 20
    .forEach(({ element, selector, width, overflow, rightEdge }) => {
      console.log(`\n${selector}`);
      console.log(`  Element width: ${width}px`);
      console.log(`  Right edge: ${rightEdge}px (${rightEdge > windowWidth ? `⚠️ ${rightEdge - windowWidth}px beyond viewport` : '✅ within viewport'})`);
      console.log(`  Overflow: ${overflow}px beyond window`);
      console.log(`  Element:`, element);
      console.log(`  Computed styles:`, {
        width: window.getComputedStyle(element).width,
        maxWidth: window.getComputedStyle(element).maxWidth,
        minWidth: window.getComputedStyle(element).minWidth,
        paddingLeft: window.getComputedStyle(element).paddingLeft,
        paddingRight: window.getComputedStyle(element).paddingRight,
        marginLeft: window.getComputedStyle(element).marginLeft,
        marginRight: window.getComputedStyle(element).marginRight,
        boxSizing: window.getComputedStyle(element).boxSizing,
        overflow: window.getComputedStyle(element).overflow,
        overflowX: window.getComputedStyle(element).overflowX,
        position: window.getComputedStyle(element).position,
        display: window.getComputedStyle(element).display
      });
    });
  
  console.groupEnd();
  
  // Highlight the worst offender
  if (overflowElements.length > 0) {
    const worst = overflowElements[0];
    worst.element.style.outline = '5px solid red';
    worst.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    console.log(`\n🎯 Scrolled to worst offender: ${worst.selector}`);
    console.log('(highlighted with red outline)');
  }
}

// Add keyboard shortcut: Ctrl+Shift+G to toggle grid
document.addEventListener('keydown', (e) => {
  if (e.ctrlKey && e.shiftKey && e.key === 'G') {
    e.preventDefault();
    document.body.classList.toggle('show-grid');
    const isShown = document.body.classList.contains('show-grid');
    console.log(isShown ? '📐 Grid shown' : '📐 Grid hidden');
  }
});

// Add keyboard shortcut: Ctrl+Shift+D to toggle debug mode
document.addEventListener('keydown', (e) => {
  if (e.ctrlKey && e.shiftKey && e.key === 'D') {
    e.preventDefault();
    const isDebug = document.body.classList.contains('debug-overflow');
    if (isDebug) {
      disableDebugMode();
    } else {
      enableDebugMode();
    }
  }
});

// Add keyboard shortcut: Ctrl+Shift+F to find overflow elements
document.addEventListener('keydown', (e) => {
  if (e.ctrlKey && e.shiftKey && e.key === 'F') {
    e.preventDefault();
    findOverflowElements();
  }
});

// Export functions to window for console access
if (typeof window !== 'undefined') {
  window.enableDebugMode = enableDebugMode;
  window.disableDebugMode = disableDebugMode;
  window.showBreakpoint = showBreakpoint;
  window.hideBreakpoint = hideBreakpoint;
  window.showGrid = showGrid;
  window.hideGrid = hideGrid;
  window.findOverflowElements = findOverflowElements;

  console.log('🛠️ Debug helpers loaded. Available commands:');
  console.log('  - enableDebugMode()  : Enable debug mode (Ctrl+Shift+D)');
  console.log('  - showGrid()         : Show grid overlay (Ctrl+Shift+G)');
  console.log('  - findOverflowElements() : Find overflow (Ctrl+Shift+F)');
}

